import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  getChildren,
  getStudent,
  getStudentClassrooms,
  getStudentsAndValidationRequests,
  patchStudentValidation,
  postSaveStudentClassroom,
  getCourses,
  postStudentCourses,
  studentsUnder14,
  getStudentClassroomsByCourse,
  getChildrenId,
  getStudentsFromAClass,
  deleteStudentFromClass,
  patchJoinClass,
  postChangePasswordChild,
} from 'api/student'
import { hideLoader } from 'store/actions/drawer.actions'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { TYPES_ALERTS } from 'utils/constants'
import { CLASSROOM_KEYS } from 'hooks/classroom'

export const STUDENT_KEYS = {
  CHECK_BOOK_LICENSE: 'CHECK_BOOK_LICENSE',
  CHECK_CLASS_CODE: 'CHECK_CLASS_CODE',
  CHILDREN: 'CHILDREN',
  CHILDRENS: 'CHILDRENS',
  STUDENT: 'STUDENT',
  STUDENTS_IN_CLASS: 'STUDENTS_IN_CLASS',
  STUDENTS_VALIDATIONS: 'STUDENTS_VALIDATIONS',
  STUDENT_CLASSROOMS: 'STUDENT_CLASSROOMS',
  STUDENT_CLASSROOMS_BY_COURSE: 'STUDENT_CLASSROOMS_BY_COURSE',
  STUDENT_COURSES: 'STUDENT_COURSES',
}
export const useGetStudentsAndValidationRequests = ({
  classroomId,
  enabled,
}) => {
  const dispatch = useDispatch()
  const getStudentsAndValidationRequestsAndLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getStudentsAndValidationRequests({ classroomId })
  }
  return useQuery(
    [STUDENT_KEYS.STUDENTS_VALIDATIONS, classroomId],
    getStudentsAndValidationRequestsAndLoaderHidden,
    {
      enabled,
      onSuccess: () => dispatch(hideLoader(false)),
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  )
}

export const useGetStudentsFromAClass = ({ classId, enabled }) => {
  const dispatch = useDispatch()
  const getStudentsAndValidationRequestsAndLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getStudentsFromAClass({ classId })
  }
  return useQuery(
    [STUDENT_KEYS.STUDENTS_IN_CLASS, classId],
    getStudentsAndValidationRequestsAndLoaderHidden,
    {
      enabled,
      onSuccess: () => dispatch(hideLoader(false)),
      staleTime: Infinity,
    }
  )
}

export const useGetStudent = ({ userId, enabled, onSuccess }) => {
  return useQuery(
    [STUDENT_KEYS.STUDENT, userId],
    () => getStudent({ userId }),
    {
      enabled,
      onSuccess,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  )
}

export const usePatchStudentValidation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ newStatus, updateStudentValidationRequestsIds }) =>
      patchStudentValidation({ newStatus, updateStudentValidationRequestsIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [STUDENT_KEYS.STUDENTS_VALIDATIONS],
        })
      },
    }
  )
}

export const useGetChildrens = ({ enabled = true } = {}) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetChildren = () => {
    dispatch(hideLoader(true))
    return getChildren()
  }
  return useQuery([STUDENT_KEYS.CHILDRENS], hideLoaderAndGetChildren, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const usePostSaveStudentClassroom = () => {
  return useMutation(({ classroomId, studentId }) =>
    postSaveStudentClassroom({ classroomId, studentId })
  )
}

export const useGetStudentClassrooms = ({ studentId, enabled }) => {
  return useQuery(
    [STUDENT_KEYS.STUDENT_CLASSROOMS, studentId],
    () => getStudentClassrooms(studentId),
    {
      enabled,
    }
  )
}

export const useGetStudentCourses = ({ enabled, schoolID }) => {
  const dispatch = useDispatch()
  const hideLoaderAndGetCourses = () => {
    dispatch(hideLoader(true))
    return getCourses(schoolID)
  }
  return useQuery([STUDENT_KEYS.STUDENT_COURSES], hideLoaderAndGetCourses, {
    enabled,
  })
}

export const usePostCourseStudent = () => {
  const dispatch = useDispatch()
  const postStudentCoursesLoaderHidden = (studentId, courseId) => {
    dispatch(hideLoader(true))
    return postStudentCourses(studentId, courseId)
  }
  return useMutation(
    ({ studentId, courseId }) =>
      postStudentCoursesLoaderHidden(studentId, courseId),
    {
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
    }
  )
}

export const useGetStudentClassroomsByCourse = ({
  courseId,
  schoolId,
  enabled,
}) => {
  return useQuery(
    [STUDENT_KEYS.STUDENT_CLASSROOMS, courseId, schoolId],
    () =>
      getStudentClassroomsByCourse({
        courseId,
        schoolId,
      }),
    {
      enabled,
    }
  )
}

export const useGetChildrenById = ({ childrenId, enabled }) => {
  return useQuery(
    [STUDENT_KEYS.CHILDREN, childrenId],
    () => getChildrenId(childrenId),
    {
      enabled,
      staleTime: Infinity,
    }
  )
}

export const usePostNewStudent = () => {
  const dispatch = useDispatch()
  const postNewStudentLoaderHidden = (data) => {
    dispatch(hideLoader(true))
    return studentsUnder14(data)
  }
  return useMutation((data) => postNewStudentLoaderHidden(data), {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const useRemoveStudentFromClass = () => {
  const dispatch = useDispatch()
  const hiddenLoader = ({ classId, studentId }) => {
    dispatch(hideLoader(true))
    return deleteStudentFromClass({ classId, studentId })
  }
  return useMutation(
    ({ classId, studentId }) => hiddenLoader({ classId, studentId }),
    {
      onSuccess: () => {
        dispatch(hideLoader(false))
      },
    }
  )
}

export const usePatchJoinClass = () => {
  const queryClient = useQueryClient()
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const patchJoinClassAndHideLoader = ({
    classCode,
    licenseCode,
    studentId,
  }) => {
    dispatch(hideLoader(true))
    return patchJoinClass({ classCode, licenseCode, studentId })
  }
  return useMutation(
    ({ classCode, licenseCode, studentId }) =>
      patchJoinClassAndHideLoader({ classCode, licenseCode, studentId }),
    {
      onError: () => {
        addFeedback({
          dataTestId: 'error-feedback-student-join-class',
          id: 'error-feedback-student-join-class',
          text: t('Ha ocurrido un problema'),
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: (_data, { studentId }) => {
        dispatch(hideLoader(false))
        addFeedback({
          dataTestId: 'success-feedback-student-join-class',
          id: 'success-feedback-student-join-class',
          text: t('Te has unido correctamente a la clase'),
          variant: TYPES_ALERTS.SUCCESS,
        })
        queryClient.invalidateQueries({
          queryKey: [CLASSROOM_KEYS.CLASSES, studentId],
        })
      },
    }
  )
}

export const useSetPasswordChild = () => {
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const hideLoaderAndCallApi = ({ childrenId, newPassword1, newPassword2 }) => {
    dispatch(hideLoader(true))
    return postChangePasswordChild({ childrenId, newPassword1, newPassword2 })
  }
  return useMutation(
    ({ childrenId, newPassword1, newPassword2 }) =>
      hideLoaderAndCallApi({ childrenId, newPassword1, newPassword2 }),
    {
      onError: () => {
        addFeedback({
          dataTestId: 'error-feedback-update-student',
          id: 'error-feedback-update-student',
          text: t(
            'Lo sentimos, pero algo ha fallado No se han podido guardar los cambios'
          ),
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: () => {
        dispatch(hideLoader(false))
        addFeedback({
          dataTestId: 'success-feedback-update-student',
          id: 'success-feedback-update-student',
          text: t('Los cambios se han realizado con éxito'),
          variant: TYPES_ALERTS.SUCCESS,
        })
      },
    }
  )
}
